/**
 * Pure utilities for the Matches page (rapid click, error message, result helpers).
 */

/** Time window in ms within which a second click is treated as rapid. */
export const RAPID_CLICK_WINDOW_MS = 1000;

/**
 * Build a stable key for a user–match pair (e.g. for tracking last click time).
 */
export function getMatchKey(currentUserId: string, matchId: string): string {
  return `${currentUserId}-${matchId}`;
}

/**
 * Returns true if the last click was within the rapid-click window.
 */
export function isRapidClick(
  lastClickTime: number | undefined,
  now: number,
  windowMs: number = RAPID_CLICK_WINDOW_MS
): boolean {
  return lastClickTime !== undefined && now - lastClickTime < windowMs;
}

/**
 * Compose a single error message from matches-page query/creation errors.
 */
export function getMatchesErrorMessage(errors: {
  newMatchesError?: Error | null;
  allMatchesError?: Error | null;
  createChatError?: Error | null;
}): string {
  const { newMatchesError, allMatchesError, createChatError } = errors;
  return newMatchesError?.message || allMatchesError?.message || createChatError?.message || "Failed to load matches";
}

/**
 * Result shape from concurrent match handlers (e.g. handleConcurrentLikes, handleNetworkFailure).
 */
export interface ConcurrentMatchResultLike {
  isMatch?: boolean;
  chatId?: string;
  error?: string | undefined;
  isDuplicate?: boolean;
}

/**
 * Returns the chat ID from a concurrent match result (or duplicate result) if present.
 */
export function getChatIdFromResult(result: ConcurrentMatchResultLike): string | undefined {
  return result.chatId && typeof result.chatId === "string" ? result.chatId : undefined;
}
